<template>
	<PageTitleComponent v-if="!isEmbedded && !hideTitle">Book Purchase</PageTitleComponent>

	<div
		class="pre-checkout-container"
		:class="{ 'checkout-mounted': checkoutMounted }"
	>
		<section
			id="user-details"
			class="band"
			v-if="!showPartnerOnlyNotice"
		>
			<form action="#">
				<div class="form-group cols cols-gutters cols-fit">
					<div class="col col-6">
						<label
							class="label"
							for="email"
						>
							<strong>Email</strong>
							<div class="required">*</div>
						</label>
						<input
							id="email"
							type="email"
							name="email"
							placeholder="example@example.com"
							autocomplete="email"
							v-model="person.email"
						/>
					</div>
					<div class="col col-6">
						<label
							class="label"
							for="school-name"
						>
							<strong>School/Organisation name</strong>
							<div class="required">*</div>
						</label>
						<input
							id="school-name"
							type="text"
							name="school-name"
							placeholder="Crunchem Hall Primary School"
							autocomplete="organization"
							v-model="person.schoolName"
						/>
					</div>
				</div>
				<div class="form-group cols cols-gutters cols-fit">
					<div class="col col-6">
						<label
							class="label"
							for="country"
						>
							<strong>Shipping country</strong>
							<div class="required">*</div>
						</label>
						<div class="select-container">
							<select
								id="country"
								v-model="person.country"
								name="country"
							>
								<option
									:value="null"
									selected
									disabled
								>
									Choose your shipping country
								</option>
								<option
									v-for="(v, k) in shippingCountries"
									:key="k"
									:value="k"
								>
									{{ v }}
								</option>
							</select>
						</div>
					</div>
					<div class="col col-6 no-label">
						<label
							class="label checkbox-label"
							for="newsletter-opt-in"
						>
							<input
								id="newsletter-opt-in"
								type="checkbox"
								name="newsletter-opt-in"
								v-model="person.newsletterOptIn"
							/>
							<span>Get Adam Voigt's weekly insights via his Home Truths newsletter.</span>
						</label>
					</div>
				</div>
			</form>
		</section>

		<section
			class="band"
			v-if="isInternationalOrder"
		>
			<h3>Email us for international orders</h3>
			<p>For orders outside Australia and New Zealand, please email us at <a href="mailto:info@realschools.com.au">info@realschools.com.au</a>.</p>
		</section>

		<section
			class="band"
			v-if="!isInternationalOrder && showPartnerOnlyNotice"
		>
			<h3>Available to partner schools</h3>
			<p>
				The Blue Book for Champions and The Black Book for Principals are part of the Real Schools partnership. Please
				<router-link
					v-if="!isEmbedded"
					:to="{ name: 'login' }"
					>sign in to the partner portal</router-link
				><span v-else>sign in to the partner portal</span> to order additional copies, or email us at <a href="mailto:info@realschools.com.au">info@realschools.com.au</a>.
			</p>
		</section>

		<section
			id="books"
			class="band"
			v-if="!isInternationalOrder && !showPartnerOnlyNotice"
		>
			<div class="books">
				<template
					v-for="book in visibleBooks"
					:key="book.id"
				>
					<h3
						v-if="book.id === firstRestBookId"
						class="books-divider"
					>
						Also available
					</h3>
					<article
						:id="book.id"
						class="book"
						:class="{ 'book-secondary': isRestBook(book) }"
					>
						<div class="book-image">
							<img
								:src="book.image"
								:alt="book.imageAlt"
							/>
						</div>
						<div class="book-details">
							<h2 class="book-title">{{ book.title }}</h2>
							<p class="book-price">
								Price: {{ formatPrice(book.price) }}<small> ({{ shouldChargeGst ? 'inc GST' : 'GST-Free' }})</small>
							</p>
							<p>Author: {{ book.author }}</p>
							<p class="book-description">{{ book.description }}</p>
							<div class="book-button">
								<QuantityComponent
									v-model="book.quantity"
									:show-add-to-cart="true"
									:max="book.maxQuantity"
								/>
								<p
									v-if="book.maxQuantity !== undefined && book.quantity >= book.maxQuantity"
									class="book-max-notice"
								>
									For orders of more than {{ book.maxQuantity }} copies, please contact <a href="mailto:info@realschools.com.au">info@realschools.com.au</a>.
								</p>
							</div>
						</div>
					</article>
				</template>
			</div>
			<div class="cart">
				<div class="cart-inner">
					<h3 class="cart-total">
						Total <small>(AUD, {{ shouldChargeGst ? 'inc GST' : 'GST-Free' }})</small>
					</h3>
					<h2 class="cart-price">{{ formatPrice(totalPrice) }}</h2>
					<small>{{ cartShippingNotice }}</small>

					<button
						class="btn"
						@click="handleCheckout"
						:disabled="!canMountCheckout"
					>
						Go to payment
					</button>

					<div v-opacity="totalBooks <= 0">
						<p>Choose the books you'd like to purchase.</p>
					</div>

					<div v-opacity="!formIsValid">
						<p>Please fill out your details at the top of the page.</p>
					</div>
				</div>
			</div>
		</section>
	</div>

	<section
		id="checkout-container"
		class="container container-expanded container-no-pad band"
		:class="{ 'checkout-mounted': checkoutMounted }"
	>
		<div
			id="checkout"
			ref="checkout"
		></div>

		<div
			id="return"
			class="text-center"
			v-if="checkoutMounted"
		>
			<a
				href="#"
				@click.prevent="goBackToCart"
				>Go back to update order</a
			>
		</div>
	</section>

	<LoadingComponent
		v-if="!checkoutMounted && checkoutMounting"
		:loading="true"
		:full-screen="true"
	/>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { mapState } from 'pinia';
import { loadStripe, type Stripe, type StripeEmbeddedCheckout } from '@stripe/stripe-js';
import { request } from '@/services/requestService';
import LoadingComponent from '@/components/shared/LoadingComponent.vue';
import PageTitleComponent from '@/components/shared/PageTitleComponent.vue';
import restoringTeachingBookCover from '@/images/restoring-teaching.jpg';
import realQuestionsBookCover from '@/images/real-questions.png';
import teacherWellbeingBookCover from '@/images/teacher-wellbeing.png';
import blueBookCover from '@/images/blue-book.webp';
import blackBookCover from '@/images/black-book.webp';
import studentBehaviourBookCover from '@/images/ttasb-book.webp';
import { useUser } from '@/stores/userStore';
import QuantityComponent from '@/components/shared/QuantityComponent.vue';
import { isEmbedded } from '@/helpers';

type BookId = 'restoring-teaching' | 'teacher-wellbeing' | 'real-questions' | 'blue-book' | 'black-book' | 'student-behaviour';

interface Book {
	id: BookId;
	title: string;
	author: string;
	description: string;
	image: string;
	imageAlt: string;
	price: number;
	quantity: number;
	maxQuantity?: number;
}

// inc-GST prices; formatPrice() divides by 1.1 for NZ to show the ex-GST equivalent
const REGULAR_BOOK_PRICE = 19.95; // $19.95 inc GST
const HANDBOOK_PRICE = 9.5; // $9.50 inc GST
const RT_PRICE = 29.95; // $29.95 inc GST
const TTASB_PRICE = 24.95; // $24.95 inc GST

const maxBookQuantity = 100;

// ?black-book&rest — show the named book(s) first, then the remaining titles de-emphasised
const restQueryKey = 'rest';

// Partner-benefit titles: every partner school gets free copies, so these are only
// ever offered inside the portal to a signed-in user. Embeds on realschools.com.au
// are cross-site iframes that don't carry the session, so they never appear there.
const partnerOnlyBookIds: BookId[] = ['blue-book', 'black-book'];

let stripeLoader: Promise<Stripe | null> = Promise.resolve(null);

export default defineComponent({
	components: { LoadingComponent, PageTitleComponent, QuantityComponent },
	directives: {
		opacity: {
			mounted(el, binding) {
				el.style.opacity = binding.value ? '1' : '0';
			},
			updated(el, binding) {
				el.style.opacity = binding.value ? '1' : '0';
			},
		},
	},
	props: {
		initBooks: {
			type: Array as PropType<BookId[]>,
			default: () => [],
		},
		initRedirect: {
			type: String,
			default: '',
		},
		initShowRest: {
			type: Boolean,
			default: false,
		},
		hideTitle: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			isEmbedded,
			// IDs of the featured books; empty = show all
			books: [...this.initBooks] as BookId[],
			// also list the books not featured, de-emphasised
			showRest: this.initShowRest,
			allBooks: [
				{
					id: 'restoring-teaching' as BookId,
					title: 'Restoring Teaching',
					author: 'Adam Voigt',
					description: 'Building on his Restorative Practice "RP2.0" methodology, this edition includes practical resources and templates for educators developed by Adam, alongside his practical, no-nonsense advice to help you implement restoratives practice in your classroom.',
					image: restoringTeachingBookCover,
					imageAlt: 'Restoring Teaching book cover',
					price: RT_PRICE,
					quantity: 0,
				},
				{
					id: 'student-behaviour' as BookId,
					title: 'The Truth About Student Behaviour',
					author: 'Adam Voigt',
					description: 'Simple shifts to transform classroom chaos into calm.',
					image: studentBehaviourBookCover,
					imageAlt: 'The Truth About Student Behaviour book cover',
					price: TTASB_PRICE,
					quantity: 0,
				},
				{
					id: 'teacher-wellbeing' as BookId,
					title: 'Tackling Teacher Wellbeing',
					author: 'Adam Voigt',
					description: 'Teacher Wellbeing needs addressing in a way that supports and establishes embedded approaches rather than reactive responses. We need a more thorough, wholesome and reliable approach that values both the teacher and the person, one that combines both subjective and psychological wellbeing and one that is formed to help individuals and collectives improve their wellbeing both in and out of the classroom.',
					image: teacherWellbeingBookCover,
					imageAlt: 'Tackling Teacher Wellbeing book cover',
					price: REGULAR_BOOK_PRICE,
					quantity: 0,
				},
				{
					id: 'real-questions' as BookId,
					title: 'Real Questions for Real School Leaders',
					author: 'Adam Voigt',
					description: 'Working more like a journal than a book that you read, Adam has carefully designed 100 questions designed to refocus your attention on your School Culture – in a fun, productive and highly reflective way.',
					image: realQuestionsBookCover,
					imageAlt: 'Real Questions for Real School Leaders book cover',
					price: REGULAR_BOOK_PRICE,
					quantity: 0,
				},
				{
					id: 'blue-book' as BookId,
					title: 'The Blue Book for Champions',
					author: 'Kirsty Lush',
					description: 'A blueprint for championing a restorative cultural shift in your school.',
					image: blueBookCover,
					imageAlt: 'The Blue Book for Champions book cover',
					price: HANDBOOK_PRICE,
					quantity: 0,
					maxQuantity: 5,
				},
				{
					id: 'black-book' as BookId,
					title: 'The Black Book for Principals',
					author: 'Simon Dewar',
					description: 'What you need to know about leading culture the Real Schools way.',
					image: blackBookCover,
					imageAlt: 'The Black Book for Principals book cover',
					price: HANDBOOK_PRICE,
					quantity: 0,
					maxQuantity: 5,
				},
			] as Book[],
			checkout: null as StripeEmbeddedCheckout | null,
			checkoutMounting: false,
			checkoutMounted: false,
			person: {
				email: '',
				schoolName: '',
				country: 'au',
				newsletterOptIn: true,
			},
			shippingCountries: {
				au: 'Australia',
				nz: 'New Zealand',
				other: 'Other International',
			},
			redirect: this.initRedirect,
		};
	},
	computed: {
		// everything on sale to this visitor, before the featured/rest split
		availableBooks(): Book[] {
			if (this.isLoggedIn) {
				return this.allBooks;
			}
			return this.allBooks.filter((book) => !partnerOnlyBookIds.includes(book.id));
		},
		bookGroups(): { featured: Book[]; rest: Book[] } {
			if (!this.books.length) {
				return { featured: this.availableBooks, rest: [] };
			}

			const featured = this.availableBooks.filter((book) => this.books.includes(book.id));
			const rest = this.showRest ? this.availableBooks.filter((book) => !this.books.includes(book.id)) : [];

			// Every requested title was partner-only and this visitor isn't signed in.
			// Promote whatever is left rather than leading with "Also available" under nothing.
			if (!featured.length) {
				return { featured: rest, rest: [] };
			}

			return { featured, rest };
		},
		featuredBooks(): Book[] {
			return this.bookGroups.featured;
		},
		// the remaining titles, only when a subset was featured and `rest` was asked for
		restBooks(): Book[] {
			return this.bookGroups.rest;
		},
		visibleBooks(): Book[] {
			return [...this.featuredBooks, ...this.restBooks];
		},
		firstRestBookId(): BookId | null {
			return this.restBooks[0]?.id ?? null;
		},
		// only reachable by asking for partner-only titles while signed out
		showPartnerOnlyNotice(): boolean {
			return !this.visibleBooks.length;
		},
		cartShippingNotice(): string {
			if (this.totalBooks >= 100) {
				return 'For orders of 100 or more books, please contact us at info@realschools.com.au for a freight quote.';
			}
			return 'Shipping calculated on next step';
		},
		isInternationalOrder(): boolean {
			return this.person.country !== '' && this.person.country === 'other';
		},
		totalBooks(): number {
			return this.visibleBooks.reduce((total, book) => total + book.quantity, 0);
		},
		totalPrice(): number {
			return this.visibleBooks.reduce((total, book) => total + book.quantity * book.price, 0);
		},
		formIsValid(): boolean {
			return this.person.email.trim().length > 0 && this.person.schoolName.trim().length > 0;
		},
		canMountCheckout(): boolean {
			return this.totalBooks > 0 && this.totalBooks < 100 && this.formIsValid;
		},
		shouldChargeGst(): boolean {
			return this.person.country === 'au';
		},
		...mapState(useUser, ['isLoggedIn']),
	},
	async beforeMount() {
		stripeLoader = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

		const userStore = useUser();
		this.person.schoolName = userStore.school?.post_title || '';
		this.person.email = userStore.email || '';
		this.person.schoolName = userStore.school?.post_title || '';
	},
	mounted() {
		if (this.isEmbedded) {
			this.applyBooksFromQuery();
			this.applyQuantitiesFromQuery();

			if (this.$route.query.redirect && typeof this.$route.query.redirect === 'string') {
				this.redirect = this.$route.query.redirect;
			}
		}
	},
	beforeUnmount() {
		this.checkout?.destroy();
	},
	methods: {
		applyBooksFromQuery() {
			if (!this.$route.query || Object.keys(this.$route.query).length === 0) {
				return;
			}
			this.books = this.allBooks.filter((book) => Object.prototype.hasOwnProperty.call(this.$route.query, book.id)).map((book) => book.id);
			this.showRest = Object.prototype.hasOwnProperty.call(this.$route.query, restQueryKey);
		},
		isRestBook(book: Book): boolean {
			return this.restBooks.some((restBook) => restBook.id === book.id);
		},
		getQueryValue(value: unknown): string | undefined {
			if (Array.isArray(value)) {
				for (const entry of value) {
					if (typeof entry === 'string') {
						return entry;
					}
				}
				return undefined;
			}
			return typeof value === 'string' ? value : undefined;
		},
		parseQuantity(value: string | undefined): number | null {
			if (!value) {
				return null;
			}
			if (!/^\d+$/.test(value)) {
				return null;
			}
			const numericValue = Number.parseInt(value, 10);
			if (Number.isNaN(numericValue) || numericValue < 0) {
				return null;
			}
			return Math.min(numericValue, maxBookQuantity);
		},
		applyQuantitiesFromQuery() {
			// availableBooks, not allBooks: a quantity on a hidden partner-only title
			// would otherwise sit in the cart unseen and reach checkout
			for (const book of this.availableBooks) {
				const quantityValue = this.getQueryValue(this.$route.query[book.id]);
				const quantity = this.parseQuantity(quantityValue);
				if (quantity !== null) {
					book.quantity = quantity;
				}
			}
		},
		formatPrice(price: number): string {
			if (!this.shouldChargeGst) {
				price /= 1.1; // NZ: derive ex-GST equivalent from the inc-GST stored price
			}
			return `$${price.toFixed(2)}`;
		},
		async handleCheckout() {
			if (this.totalBooks >= 100) {
				alert('For orders of 100 or more books, please contact us at info@realschools.com.au for a freight quote.');
				return;
			}

			this.checkoutMounting = true;

			// anything not on sale to this visitor is sent as 0, never as a stale quantity
			const availableIds = this.availableBooks.map((b) => b.id);
			const books = Object.fromEntries(this.allBooks.map((b) => [b.id, availableIds.includes(b.id) ? b.quantity : 0]));

			const res = await request<StripePaymentIntentResponse>('publicBookPurchase', 'getBookPaymentIntent', {
				books,
				email: this.person.email,
				schoolName: this.person.schoolName,
				country: this.person.country,
				newsletterOptIn: this.person.newsletterOptIn,
				redirect: this.redirect,
			});

			// every early return clears checkoutMounting, or the full-screen loading
			// overlay stays up and the page is stuck until the user reloads
			if (!res.success) {
				// the backend sends a specific message when it knows why (e.g. a title
				// that can't be sold online); fall back to the generic code otherwise
				this.checkoutMounting = false;
				alert(res.message || 'Sorry, there was an error processing your request. Please contact info@realschools.com.au quoting E011.');
				return;
			}
			const clientSecret = res.data.clientSecret;

			const stripe = await stripeLoader;
			if (!stripe) {
				this.checkoutMounting = false;
				alert('Sorry, there was an error processing your request. Please contact info@realschools.com.au quoting E901.');
				return;
			}

			this.checkout = await stripe.initEmbeddedCheckout({
				clientSecret,
			});

			this.checkout.mount('#checkout');

			setTimeout(() => {
				(this.$refs?.checkout as HTMLElement | null)?.scrollIntoView({ behavior: 'smooth' });
			}, 100);

			this.checkoutMounting = false;
			this.checkoutMounted = true;
		},
		goBackToCart() {
			window.scrollTo({ top: 0, behavior: 'smooth' });

			this.checkoutMounted = false;
			this.checkout?.destroy();
			this.checkout = null;
		},
	},
});

interface StripePaymentIntentResponse {
	clientSecret: string;
}
</script>

<style scoped lang="scss">
.pre-checkout-container {
	&.checkout-mounted {
		opacity: 0.5;
		pointer-events: none;
		user-select: none;
	}
}

#user-details {
	padding-bottom: 0;
}

#books {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	gap: 40px 60px;

	.books {
		display: flex;
		flex: 1;
		flex-flow: row wrap;
		justify-content: center;
		gap: 20px;
		width: clamp(300px, 100%, 800px);

		.books-divider {
			margin: 20px 0 0;
			border-top: 1px solid rgb(var(--rs-color-brand-grey-lighter));
			padding-top: 20px;
			width: 100%;
			color: rgb(var(--rs-color-brand-grey-dark));
			font-size: 14px;
			font-family: var(--rs-typography-font-body);
			letter-spacing: 1px;
			text-transform: uppercase;
		}

		.book {
			display: flex;
			flex-flow: row wrap;
			gap: 40px;
			border: 1px solid rgb(var(--rs-color-brand-navy));
			border-radius: 4px;
			background-color: rgb(var(--rs-color-brand-navy));
			padding: 20px;
			width: 100%;
			color: white;

			.book-image {
				img {
					max-width: 150px;
				}
			}

			.book-details {
				display: flex;
				flex: 1;
				flex-direction: column;
				min-width: 300px;

				.book-title {
					margin-bottom: 10px;

					// mustard, not cherry: cherry on navy is only 2.4:1 (fails AA). Matches the
					// navy-section heading pattern used on the VPA/masterclass pages.
					color: rgb(var(--rs-color-brand-mustard));
					font-size: 30px;
				}

				.book-price {
					font-size: 24px;
				}

				.book-button {
					margin-top: auto;

					.book-max-notice {
						opacity: 0.85;
						margin-top: 8px;
						font-size: 13px;

						a {
							color: inherit;
						}
					}
				}
			}

			&#restoring-teaching {
				.book-image {
					img {
						border: 1px solid white;
					}
				}
			}

			// the "also available" titles: same card, dialled well back
			&.book-secondary {
				gap: 25px;
				border-color: rgb(var(--rs-color-brand-grey-lighter));
				background-color: white;
				padding: 15px 20px;
				color: rgb(var(--rs-typography-font-body-color));

				.book-image {
					img {
						max-width: 90px;
					}
				}

				.book-details {
					min-width: 240px;

					.book-title {
						color: rgb(var(--rs-color-brand-navy));
						font-size: 22px;
					}

					.book-price {
						font-size: 18px;
					}

					p {
						font-size: 16px;
					}

					// keep the card short so it stays subordinate to the featured book
					.book-description {
						display: -webkit-box;
						-webkit-box-orient: vertical;
						-webkit-line-clamp: 3;
						line-clamp: 3;
						overflow: hidden;
					}
				}

				&#restoring-teaching {
					.book-image {
						img {
							border-color: rgb(var(--rs-color-brand-grey-lighter));
						}
					}
				}
			}
		}
	}

	.cart {
		width: 200px;

		.cart-inner {
			position: sticky;
			top: 20px;

			.cart-total {
				margin-bottom: -3px;
				font-size: 18px;
				font-family: var(--rs-typography-font-body);
				letter-spacing: 1px;
				text-transform: uppercase;
			}

			.cart-price {
				margin: 0 0 10px 0;
				font-size: 52px;
			}

			.btn {
				margin-bottom: 40px;
			}
		}
	}
}

#checkout-container {
	&.checkout-mounted {
		background-color: rgb(var(--rs-color-brand-navy));

		#checkout {
			margin-top: 20px;
			padding: 40px 0;
			min-height: 200px;
		}

		#return {
			margin-top: 20px;
			font-size: 14px;

			a {
				color: white;
			}
		}
	}
}
</style>
